import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';
import { Truck, ArrowLeft, Lock, CheckCircle } from 'lucide-react';
import { getErrorMessage } from '../lib/utils';
import logoIcon from '../assets/logo-icon.png';
import logoWordmark from '../assets/logo-wordmark.png';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
      toast.success('Contraseña restablecida correctamente');
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudo restablecer la contraseña'));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center p-8" data-testid="reset-password-page">
        <div className="w-full max-w-md text-center">
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight mb-4">
            Enlace inválido
          </h1>
          <p className="text-muted-foreground mb-6">
            Este enlace para restablecer la contraseña es inválido o ha expirado.
          </p>
          <Link to="/forgot-password">
            <Button className="btn-accent">Solicitar nuevo enlace</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center p-8" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <img
              src={logoIcon}
              alt=""
              aria-hidden="true"
              className="h-14 w-auto"
            />
            <img
              src={logoWordmark}
              alt="VehiMax - Todos los Vehículos. Una Plataforma"
              className="h-8 w-auto rounded-sm"
            />
          </Link>

          {!success ? (
            <>
              <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">Restablecer contraseña</h1>
              <p className="text-muted-foreground mt-2">Ingresa tu nueva contraseña.</p>
            </>
          ) : (
            <div className="flex items-start gap-3">
              <CheckCircle className="h-6 w-6 text-green-600 shrink-0 mt-1" />
              <div>
                <h1 className="font-heading text-3xl font-bold uppercase tracking-tight">
                  Contraseña restablecida
                </h1>
                <p className="text-muted-foreground mt-2">
                  Tu contraseña se cambió correctamente.
                </p>
              </div>
            </div>
          )}
        </div>

        {!success ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold uppercase tracking-wide">Nueva contraseña</Label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="h-12"
                placeholder="Mínimo 6 caracteres"
                testId="reset-password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-sm font-semibold uppercase tracking-wide">Confirma la contraseña</Label>
              <PasswordInput
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="h-12"
                placeholder="Repite tu contraseña"
                testId="reset-confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              className="btn-accent w-full h-12"
              disabled={loading}
              data-testid="reset-submit-btn"
            >
              {loading ? 'Restableciendo...' : 'Restablecer contraseña'}
              <Lock className="ml-2 h-4 w-4" />
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              <Link to="/login" className="text-accent font-semibold hover:underline">
                <ArrowLeft className="inline h-3 w-3 mr-1" />
                Volver a iniciar sesión
              </Link>
            </p>
          </form>
        ) : (
          <div className="space-y-4">
            <Button
              className="btn-accent w-full h-12"
              onClick={() => navigate('/login')}
              data-testid="go-to-login-btn"
            >
              Ir a iniciar sesión
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
